// composite.form.v1
//
// A thin wrapper that provides consistent spacing for a form group:
// title + description + body (fields) + footer (actions). Plugins
// drop their fields in as children; the registry owns the rhythm.

import { type JSX, Show, splitProps } from "solid-js"

export interface FormProps {
  title?: JSX.Element
  description?: JSX.Element
  children: JSX.Element
  actions?: JSX.Element
  onSubmit?: (event: SubmitEvent) => void
  class?: string
}

export function Form(props: FormProps) {
  const [local, rest] = splitProps(props, [
    "title",
    "description",
    "children",
    "actions",
    "onSubmit",
    "class",
  ])

  return (
    <form
      data-registry-id="composite.form"
      onSubmit={local.onSubmit}
      classList={{
        "flex flex-col gap-4 min-w-0": true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <Show when={local.title || local.description}>
        <div class="flex flex-col gap-1">
          <Show when={local.title}>
            <div class="text-15-medium text-text-strong">{local.title}</div>
          </Show>
          <Show when={local.description}>
            <div class="text-13-regular text-text-base">{local.description}</div>
          </Show>
        </div>
      </Show>

      <div class="flex flex-col gap-3 min-w-0">{local.children}</div>

      <Show when={local.actions}>
        <div class="flex items-center justify-end gap-2 pt-1">{local.actions}</div>
      </Show>
    </form>
  )
}

// Field wrapper for in-form labelled controls.
export interface FormFieldProps {
  label: string
  htmlFor?: string
  description?: string
  error?: string
  required?: boolean
  children: JSX.Element
}

export function FormField(props: FormFieldProps) {
  return (
    <div data-registry-id="composite.form.field" class="flex flex-col gap-1.5 min-w-0">
      <label for={props.htmlFor} class="text-13-medium text-text-strong flex items-center gap-1">
        <span>{props.label}</span>
        <Show when={props.required}>
          <span aria-hidden="true" class="text-icon-critical-base">
            *
          </span>
        </Show>
      </label>
      {props.children}
      <Show when={props.description && !props.error}>
        <span class="text-12-regular text-text-base">{props.description}</span>
      </Show>
      <Show when={props.error}>
        <span class="text-12-regular text-icon-critical-base">{props.error}</span>
      </Show>
    </div>
  )
}
